import { Box, CircularProgress, Slider } from "@mui/material";
import React, { useRef } from "react";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useEffect, useState } from "react";
import { PlayToneIcon } from "../../../../Custom-imeges/VideoSuite/PlayToneIcon";
import { soundEffectVoiceData } from "../../../../Types/VideoSuite-Types";
import SoundEffectFilter from "./SoundEffectFilter";
import {
  GetSoundEffectRequest,
} from "../../../../store/Apis/SuitesApps/VideoSuiteApi";
import useDebounce from "../../../main-page/components/get-conversation/useDebounce";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  height: 620,
  bgcolor: "var(--sub_background6)",
  boxShadow: 24,
  outline: "none",
  borderRadius: "12px",
};
interface SelectSoundEffectModalProps {
  open: boolean;
  isLoading?: boolean;
  handleClose: () => void;
  voices: soundEffectVoiceData[];
  addSoundEffectAction: (soundEffectId: string) => void;
  setSoundEffectParams?: (data: GetSoundEffectRequest) => void;
}
const SelectSoundEffectModal = ({
  open,
  handleClose,
  voices,
  addSoundEffectAction,
  setSoundEffectParams,
  isLoading,
}: SelectSoundEffectModalProps) => {
  const [playingAudio, setPlayingAudio] = useState<HTMLAudioElement | null>(
    null
  );
  const [currentlySelectedVoice, setCurrentlySelectedVoice] = useState<
    soundEffectVoiceData | undefined
  >();
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(
    null
  );
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement | null }>({});
  const [durations, setDurations] = useState<{ [key: string]: number }>({});
  const [currentTimes, setCurrentTimes] = useState<{ [key: string]: number }>(
    {}
  );
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchInputValue, 1000);

  useEffect(() => {
    const loadDurations = async () => {
      const newDurations: { [key: string]: number } = {};

      await Promise.all(
        voices.map((voice) => {
          return new Promise<void>((resolve) => {
            const audio = new Audio(voice.url);
            audio.preload = "metadata";

            audio.onloadedmetadata = () => {
              newDurations[voice.id] = audio.duration;
              resolve();
            };

            audio.onerror = () => {
              console.warn("Failed to load duration for:", voice.url);
              newDurations[voice.id] = 0;
              resolve();
            };
          });
        })
      );

      setDurations((prev) => ({ ...prev, ...newDurations }));
    };

    if (voices?.length > 0) {
      loadDurations();
    }
  }, [voices]);

  useEffect(() => {
    if (setSoundEffectParams && searchInputValue)
      setSoundEffectParams({
        search: searchInputValue,
      });
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const interval = setInterval(() => {
      Object.keys(audioRefs.current).forEach((id) => {
        const audio = audioRefs?.current[id];
        if (audio && !audio.paused) {
          setCurrentTimes((prev) => ({
            ...prev,
            [id]: audio.currentTime,
          }));
        }
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const handleSliderChange = (voiceId: string, value: number) => {
    const audio = audioRefs.current[voiceId];
    if (audio) {
      audio.currentTime = value;
      setCurrentTimes((prev) => ({
        ...prev,
        [voiceId]: value,
      }));
    }
  };

  const handlePlayVoice = (voice: soundEffectVoiceData) => {
    const audio = audioRefs.current[voice.id];
    if (!audio) {
      console.warn("Audio element not found");
      return;
    }
    // update the source manually
    audio.src = voice.url;
    audio.load();
    // Pause currently playing audio if it's the same one
    if (playingAudio && currentlySelectedVoice?.id === voice.id) {
      playingAudio.pause();
      playingAudio.currentTime = 0;
      setCurrentlyPlayingId(null);
      setPlayingAudio(null);
      return;
    }
    // Pause any other playing audio
    if (playingAudio && playingAudio !== audio) {
      playingAudio.pause();
      playingAudio.currentTime = 0;
    }
    audio
      .play()
      .then(() => {
        setPlayingAudio(audio);
        setCurrentlyPlayingId(voice.id);
      })
      .catch((err) => {
        console.error("Audio play failed:", err);
        setCurrentlyPlayingId(null);
      });

    audio.onended = () => {
      setCurrentlyPlayingId(null);
      setCurrentTimes((prev) => ({
        ...prev,
        [voice.id]: durations[voice.id] || 0,
      }));
    };
  };

  const handleStActiveVoice = (voice: soundEffectVoiceData) => {
    setCurrentlySelectedVoice(voice);
    handlePlayVoice(voice);
  };

  const handleCloseWithCleanup = () => {
    if (playingAudio) {
      playingAudio.pause();
      playingAudio.currentTime = 0;
      setPlayingAudio(null);
      setCurrentlyPlayingId(null);
    }
    handleClose();
  };
  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          ...style,
          "@media (max-width: 900px)": {
            width: "98%",
          },
        }}
      >
        <Box className="avatar-modal-header">
          <Typography>Add sound effect</Typography>
          <IconButton
            aria-label="close"
            color="inherit"
            onClick={handleCloseWithCleanup}
          >
            <ClearIcon />
          </IconButton>
        </Box>

        <Box
          className="avatar-modal-body"
          sx={{
            padding: "16px 34px 24px 34px",
            gap: "16px",
            overflowY: "unset",
          }}
        >
          <SoundEffectFilter
            setSoundEffectParams={setSoundEffectParams}
            soundEffectData={voices}
            searchInputValue={searchInputValue}
            setSearchInputValue={setSearchInputValue}
          />
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: "400",
              fontSize: "14px",
              lineHeight: "24px",
              letterSpacing: "0.1px",
              opacity: "0.7",
              color: "--Main_icons_color",
            }}
          >
            Most popular sounds
          </Typography>
          <Box className="avatar-voices-container">
            {isLoading ? (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={50} color="primary" />
              </Box>
            ) : !isLoading && voices?.length > 0 ? (
              voices?.map((voice, i) => (
                <Box
                  key={i}
                  className={`sound-effect-box ${
                    currentlySelectedVoice?.id === voice.id
                      ? "active-sound"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStActiveVoice(voice);
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <Box className="sound-effect-info">
                      <Box style={{ cursor: "pointer" }}>
                        {currentlyPlayingId === voice.id ? (
                          <img
                            src="/images/VideoSuite/playVoiceGIF.gif"
                            alt="Playing"
                            style={{ width: 24, height: 24 }}
                          />
                        ) : (
                          <PlayToneIcon />
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography className="avatar-tone-name ms-2">
                          {voice?.fileName}
                        </Typography>
                        <Typography
                          className="ms-2"
                          sx={{
                            fontFamily: "Roboto",
                            fontWeight: "400",
                            fontSize: "14px",
                            lineHeight: "24px",
                            letterSpacing: "0.1px",
                            textAlign: "center",
                            color: "var(--Main-icon-color)",
                            opacity: "0.7",
                          }}
                        >
                            {durations[voice.id]
                            ? new Date(durations[voice.id] * 1000)
                              .toISOString()
                              .substr(14, 5)
                            : "00:00"}
                        </Typography>
                      </Box>
                    </Box>
                    <Slider
                      value={currentTimes[voice.id] || 0}
                      min={0}
                      max={durations[voice.id] || 120}
                      step={0.1}
                      onChange={(_, value) => {
                        if (typeof value === "number") {
                          handleSliderChange(voice.id, value);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: "#00bcd4",
                        margin: "0px 16px",
                        width: "95%",
                        mt: 1,
                        zIndex: -1,
                        "& .MuiSlider-thumb": {
                          backgroundColor: "#FFF",
                          boxShadow: "none",
                        },
                        "& .MuiSlider-rail": {
                          backgroundColor: "var(--A_backgroundColor103)",
                          opacity: 1,
                        },
                        "& .MuiSlider-track": {
                          backgroundColor: "#12B6EA",
                        },
                      }}
                    />

                    <audio
                      ref={(el) => {
                        if (el) {
                          audioRefs.current[voice.id] = el;
                          el.onloadedmetadata = () => {
                            setDurations((prev) => ({
                              ...prev,
                              [voice.id]: el.duration,
                            }));
                          };
                        }
                      }}
                      src={voice.url}
                      preload="metadata"
                    />
                  </Box>
                </Box>
              ))
            ) : (
              <Box className="empty-message">No voices found</Box>
            )}
          </Box>

          <Box
            className="select-voice"
            onClick={() =>
              addSoundEffectAction(currentlySelectedVoice?.id || "")
            }
          >
            <Box className="select-voice-btn" onClick={() => {}}>
              Generate video
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SelectSoundEffectModal;
